import { BaseEntity, ID } from '@ianesthesie-kit/services';
import {
  MedicalHo,
  MedicalHoItem,
  useMedicalHoQuery
} from '@/modules/surgicalEvent/ho';
import { useMemo } from 'react';
import {
  PhysicalExaminationService,
  usePhysicalExaminationQuery
} from '@/modules/surgicalEvent/physicalExamination';
import {
  IntubationInfoService,
  useIntubationInfoQuery
} from '@/modules/surgicalEvent/intubation';
import {
  Emphasis,
  ImportantElement,
  isRisk,
  SourceType
} from '@/modules/surgicalEvent/model';
import { SurgeryInfo } from '@/modules/surgicalEvent/surgeryInfo/surgeryInfo.model';
import { useSurgeryInfoQuery } from '@/modules/surgicalEvent/surgeryInfo/surgeryInfo.slice';
import {
  AnesthesiaInfoService,
  AnesthesiaRisk,
  useAnesthesiaInfoQuery,
  useAnesthesiaRisksQuery
} from '@/modules/surgicalEvent/anesthesia';
import {
  PregnancyInfoService,
  usePregnancyInfoQuery
} from '@/modules/surgicalEvent/pregnancy';
import { createSelector } from 'reselect';
import isEmpty from 'lodash/fp/isEmpty';
import omit from 'lodash/fp/omit';
import {
  MedicalOptionGroupCode,
  MedicalOptionService
} from '@/modules/surgicalEvent/option';
import { SingleMedicalService } from '@/modules/surgicalEvent/baseMedical.service';
import { SurgeryInfoService } from '@/modules/surgicalEvent/surgeryInfo';
import {
  useCurrentPatientId,
  useDefaultQueryOptions
} from '@/modules/surgicalEvent/surgicalEvent.hooks';
import { PatientId } from '@/modules/surgicalEvent/surgicalEvent.slice';
import { Allergy, useAllergiesQuery } from '@/modules/surgicalEvent/allergy';
import { useAllergyFormatter } from '@/pages-client/patientDetails/components/useAllergyFormatter';
import { useMedicalHoItemTranslator, usePatientsAppFilter } from '@/hooks';
import { isNumeric } from '@ianesthesie-kit/core';

const medicalHoItemToImportantElement =
  (translator: ReturnType<typeof useMedicalHoItemTranslator>) =>
  (item: MedicalHoItem): ImportantElement => {
    const val = translator(item);
    const result: ImportantElement = {
      id: item.id,
      code: val,
      sourceType: item.sourceType,
      translationCode: val
    };

    return result;
  };

const useImportantHoItems = (arg: PatientId): ImportantElement[] => {
  const queryOptions = useDefaultQueryOptions();
  const translator = useMedicalHoItemTranslator();
  const selectImportantHoItems = useMemo(
    () =>
      // Return a unique selector instance for this page so that
      // the filtered results are correctly memoized
      createSelector(
        (res: any) => res.data,
        (d: MedicalHo) => {
          const data: { [k: string]: MedicalHoItem[] } = omit('id', d) as {
            [k: string]: MedicalHoItem[];
          };
          if (isEmpty(data)) {
            return [];
          }
          return Object.values(data)
            .flat()
            .filter((it) => it.emphasis === Emphasis.CRUCIAL)
            .map(medicalHoItemToImportantElement(translator));
        }
      ),
    [translator]
  );

  const { data: importantHoItems } = useMedicalHoQuery(arg, {
    ...queryOptions,
    selectFromResult: (result) => ({
      data: selectImportantHoItems(result)
    })
  });

  return importantHoItems;
};

const useCrucialAllergies = (arg: PatientId): ImportantElement[] => {
  const formatAllergy = useAllergyFormatter();
  const queryOptions = useDefaultQueryOptions();
  const selectCrucialAllergies = useMemo(
    () =>
      // Return a unique selector instance for this page so that
      // the filtered results are correctly memoized
      createSelector(
        (res: any) => res.data,
        (data: Allergy[]) => {
          if (isEmpty(data)) {
            return [];
          }
          return Object.values(data)
            .flat()
            .filter((al) => al.emphasis === Emphasis.CRUCIAL)
            .map((al) => {
              const formattedAllergy = formatAllergy(al);
              return {
                id: al.id,
                sourceType: al.sourceType,
                code: formattedAllergy,
                translationCode: formattedAllergy
              };
            });
        }
      ),
    [formatAllergy]
  );

  const { data: importantHoItems } = useAllergiesQuery(arg, {
    ...queryOptions,
    selectFromResult: (result) => ({
      data: selectCrucialAllergies(result)
    })
  });

  return importantHoItems;
};

const useHighAnesthesiaRisks = (arg: PatientId): ImportantElement[] => {
  const queryOptions = useDefaultQueryOptions();
  const selectHighRisks = useMemo(
    () =>
      // Return a unique selector instance for this page so that
      // the filtered results are correctly memoized
      createSelector(
        (res: any) => res.data,
        (anesthesiaRisk: AnesthesiaRisk) => {
          if (!anesthesiaRisk) {
            return [];
          }

          return Object.entries(anesthesiaRisk)
            .filter(([k, risk]) => k !== 'id' && risk === 'HIGH')
            .map(([k]) => ({
              id: `${anesthesiaRisk.id}-${k}`,
              code: k,
              translationCode: `doctor-dashboard.details.anesthesia.risk.${k}`
            }));
        }
      ),
    []
  );

  const { data: highRisks } = useAnesthesiaRisksQuery(arg, {
    ...queryOptions,
    selectFromResult: (result) => ({
      data: selectHighRisks(result)
    })
  });

  return highRisks;
};

function shouldPrependTranslation(
  optionCode: string,
  groupCode?: MedicalOptionGroupCode
) {
  return (
    groupCode === MedicalOptionGroupCode.PLACENTA_POSITION ||
    groupCode === MedicalOptionGroupCode.MOUTH_OPENING ||
    optionCode === 'NO' ||
    isRisk(optionCode) ||
    isNumeric(optionCode)
  );
}

const useGenericImportantItems = <
  T extends BaseEntity,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  TQuery extends Function
>(
  { patientId }: PatientId,
  baseQuery: TQuery,
  serviceGetter: (patientId: ID) => SingleMedicalService<T>,
  translationPrefix: string
): ImportantElement[] => {
  const queryOptions = useDefaultQueryOptions();
  const selectImportantItems = useMemo(
    () =>
      // Return a unique selector instance for this page so that
      // the filtered results are correctly memoized
      createSelector(
        (res: any) => res.data,
        (data: T) => {
          if (!patientId || !data) {
            return [];
          }

          const importantItems = [];
          for (const [field, groupCode] of Object.entries(
            serviceGetter(patientId).getFieldOptionGroupMapping()
          )) {
            const itemCode = (data as any)[field] as string | unknown;
            if (!itemCode) {
              continue;
            }
            if (typeof itemCode !== 'string') {
              throw new Error(`item code is not a string: ${itemCode}`);
            }
            if (!groupCode) {
              throw new Error('Group code is undefined');
            }
            if (!itemCode) {
              continue;
            }

            const medicalOption = MedicalOptionService.getInstance()
              .getGroupOptionsSync(groupCode)
              .find((o) => o.code === itemCode);
            if (medicalOption && medicalOption.emphasis === Emphasis.CRUCIAL) {
              const id = `opt_${groupCode}_${medicalOption.code}`;
              let translationCode;
              let prependWithTranslation;
              if (medicalOption.code === 'YES') {
                translationCode = `${translationPrefix}.${groupCode}`;
              } else {
                translationCode = medicalOption.translationCode;
                if (shouldPrependTranslation(medicalOption.code, groupCode)) {
                  prependWithTranslation = `${translationPrefix}.${groupCode}`;
                }
              }

              importantItems.push({
                id,
                translationCode,
                prependWithTranslation
              });
            }
          }
          return importantItems;
        }
      ),
    [patientId, serviceGetter, translationPrefix]
  );

  const { data: importantItems } = baseQuery(
    { patientId },
    {
      ...queryOptions,
      selectFromResult: (result: { data: T }) => ({
        data: selectImportantItems(result)
      })
    }
  );

  return importantItems;
};

const useUserInputImportantItems = ({
  patientId
}: PatientId): ImportantElement[] => {
  const queryOptions = useDefaultQueryOptions();
  const selectUserInputImportantItems = useMemo(
    () =>
      // Return a unique selector instance for this page so that
      // the filtered results are correctly memoized
      createSelector(
        (res: { data?: SurgeryInfo }) => res.data,
        (data?: SurgeryInfo) => {
          if (!patientId || !data?.userInputImportantElements?.length) {
            return [];
          }

          return data.userInputImportantElements.map((el) => ({
            id: el,
            code: el,
            translationCode: el,
            sourceType: SourceType.DOCTOR_INPUT
          }));
        }
      ),
    [patientId]
  );

  const { data: importantItems } = useSurgeryInfoQuery(
    { patientId },
    {
      ...queryOptions,
      selectFromResult: (result) => ({
        data: selectUserInputImportantItems(result)
      })
    }
  );

  return importantItems;
};

export const useCurrentImportantItems = () =>
  useImportantItems(useCurrentPatientId());

export const useImportantItems = (patientId: PatientId) => {
  const { filterItems } = usePatientsAppFilter();
  const hoItems = useImportantHoItems(patientId);
  const allergyItems = useCrucialAllergies(patientId);
  const intubationInfoItems = useGenericImportantItems(
    patientId,
    useIntubationInfoQuery,
    IntubationInfoService.getInstance.bind(IntubationInfoService),
    'doctor-dashboard.details.intubation'
  );
  const anesthesiaInfoItems = useGenericImportantItems(
    patientId,
    useAnesthesiaInfoQuery,
    AnesthesiaInfoService.getInstance.bind(AnesthesiaInfoService),
    'doctor-dashboard.details.anesthesia'
  );
  const physicalExaminationItems = useGenericImportantItems(
    patientId,
    usePhysicalExaminationQuery,
    PhysicalExaminationService.getInstance.bind(PhysicalExaminationService),
    'doctor-dashboard.details.physical'
  );
  const pregnancyInfoItems = useGenericImportantItems(
    patientId,
    usePregnancyInfoQuery,
    PregnancyInfoService.getInstance.bind(PregnancyInfoService),
    'doctor-dashboard.details.pregnancy'
  );
  const surgeryInfoItems = useGenericImportantItems(
    patientId,
    useSurgeryInfoQuery,
    SurgeryInfoService.getInstance.bind(SurgeryInfoService),
    'doctor-dashboard.details.surgery'
  );
  const anesthesiaRisks = useHighAnesthesiaRisks(patientId);
  const userInputImportantItems = useUserInputImportantItems(patientId);

  return useMemo(
    () =>
      filterItems([
        ...hoItems,
        ...allergyItems,
        ...intubationInfoItems,
        ...anesthesiaInfoItems,
        ...physicalExaminationItems,
        ...pregnancyInfoItems,
        ...surgeryInfoItems,
        ...anesthesiaRisks,
        ...userInputImportantItems.map((it) => ({ ...it, deletable: true }))
      ]),
    [
      allergyItems,
      anesthesiaInfoItems,
      anesthesiaRisks,
      filterItems,
      hoItems,
      intubationInfoItems,
      physicalExaminationItems,
      pregnancyInfoItems,
      surgeryInfoItems,
      userInputImportantItems
    ]
  );
};
